import type { FilePondFile } from 'filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import React, { useCallback, useMemo, useRef } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import { handleErrorToast } from 'src/components/molecules/Toast/Toast';
import { ALL_MIME_TYPES, UploadScanStatus } from 'src/types';
import {
  formatValidationError,
  validateFileUpload,
} from 'src/utils/validateFileUpload/fileValidation';
import {
  FileItemContainer,
  FilePondWrapper,
  FilesList,
  RemoveIcon,
  StyledFilePond,
  UploadedFileName,
} from './FileUpload.styles';

registerPlugin(FilePondPluginFileValidateSize);

const FILE_STATUS = { IDLE: 2, PROCESSING_COMPLETE: 5 };
const ACCEPTED_FILE_TYPES = ALL_MIME_TYPES.join(',');

type FilePondStylePanelLayout = NonNullable<
  React.ComponentProps<typeof FilePond>['stylePanelLayout']
>;

interface Props {
  setPond: (value: React.SetStateAction<FilePond | null>) => void;
  files: File[];
  server: string;
  labelIdle: string;
  onprocessfiles: () => void;
  onupdatefiles: () => void;
  onremovefile?: (error: unknown, file: FilePondFile | null) => void;
  onRemoveFileFromPond?: (file: FilePondFile) => void | Promise<void>;
  eventId?: string;
  stylePanelLayout?: FilePondStylePanelLayout | null;
}

export interface File {
  source: string;
  options: {
    type: 'input' | 'limbo' | 'local';
    file: { name: string; size: number; type: string };
    metadata?: { scanStatus?: string; qaAttachmentId?: string };
  };
  created?: string;
  updated?: string;
  status?: number;
}

export const FileUpload = ({
  setPond,
  files,
  server,
  labelIdle,
  onprocessfiles,
  onupdatefiles,
  onremovefile,
  onRemoveFileFromPond,
  eventId,
  stylePanelLayout,
}: Props) => {
  const pondRef = useRef<FilePond | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mimeTypeSet = useMemo(() => new Set(ALL_MIME_TYPES), []);

  const uploadedFiles = files.filter(
    (f) =>
      f.status === FILE_STATUS.PROCESSING_COMPLETE ||
      f.status === FILE_STATUS.IDLE,
  );

  const addValidFiles = useCallback(
    (fileList: globalThis.File[]) => {
      const failedFiles: string[] = [];

      fileList.forEach((file) => {
        // First check MIME type
        if (!mimeTypeSet.has(file.type)) {
          failedFiles.push(file.name);
          return;
        }

        // Then validate all extensions (including double extensions)
        try {
          validateFileUpload({
            filename: file.name,
            mimeType: file.type,
          });
          // If validation passes, add the file
          pondRef.current?.addFile(file);
        } catch (error) {
          failedFiles.push(file.name);
        }
      });

      // Show ONE aggregated error toast for all failed files
      if (failedFiles.length > 0) {
        handleErrorToast({
          toastMessage: 'Unsupported file type',
          toastDescription: formatValidationError(failedFiles),
        });
      }
    },
    [mimeTypeSet],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      addValidFiles(Array.from(e.dataTransfer.files));
    },
    [addValidFiles],
  );

  const handleRemoveFile = useCallback((source: string, fileName: string) => {
    if (!pondRef.current) return;
    const pondFiles = pondRef.current.getFiles();
    const match = pondFiles.find(
      (f: { serverId: string | null; filename: string; id: string }) =>
        (f.serverId && f.serverId === source) || f.filename === fileName,
    );
    if (match) {
      pondRef.current.removeFile(match.id);
    }
  }, []);

  return (
    <>
      <FilePondWrapper
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <StyledFilePond
          allowMultiple
          maxFiles={100}
          maxParallelUploads={100}
          {...(stylePanelLayout != null ? { stylePanelLayout } : {})}
          ref={(ref) => {
            setPond(ref);
            if (ref) pondRef.current = ref;
          }}
          files={files}
          server={{
            process: {
              url: server,
              ondata: (formData) => {
                if (eventId) {
                  formData.append('eventId', eventId);
                }
                return formData;
              },
            },
          }}
          allowFileSizeValidation
          labelMaxFileSizeExceeded="File is too large"
          maxFileSize="100MB"
          allowBrowse={false}
          allowDrop={false}
          labelFileProcessingError={(err) =>
            err.code === 502
              ? 'Could not connect to server'
              : 'File upload error'
          }
          labelIdle={labelIdle}
          onprocessfiles={onprocessfiles}
          onupdatefiles={onupdatefiles}
          onremovefile={(error, file) => {
            if (!error && file != null && onRemoveFileFromPond) {
              void onRemoveFileFromPond(file);
            }
            onremovefile?.(error, file);
          }}
          credits={false}
        />
        <div
          onClick={() => inputRef.current?.click()}
          style={{ position: 'absolute', inset: 0, cursor: 'pointer' }}
        />
        <input
          type="file"
          multiple
          accept={ACCEPTED_FILE_TYPES}
          style={{ display: 'none' }}
          ref={inputRef}
          onChange={(e) => {
            addValidFiles(Array.from(e.target.files || []));
            e.target.value = '';
          }}
        />
      </FilePondWrapper>
      <FilesList>
        {uploadedFiles.map((file) => {
          const scanStatus = file.options.metadata?.scanStatus as
            | UploadScanStatus
            | undefined;
          return (
            <FileItemContainer key={file.source}>
              <UploadedFileName $status={scanStatus}>
                {file.options.file.name}
              </UploadedFileName>
              <RemoveIcon
                src="/assets/close-icon-black.svg"
                onClick={() =>
                  handleRemoveFile(file.source, file.options.file.name)
                }
              />
            </FileItemContainer>
          );
        })}
      </FilesList>
    </>
  );
};
